import React, { useState, useEffect, useContext } from 'react';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import CompressorDetailsSubsection from './CompressorDetailsSubSection';
import CustomRowLabels from './CustomRowLabels';

import { ChillerLogsContext } from '../contexts/ChillerLogsContext';
import { ChillerCircuit } from '../../model';

interface CompressorDetailsSectionProps {
  circuit?: ChillerCircuit;
}

const CompressorDetailsSection: React.FunctionComponent<CompressorDetailsSectionProps> = ({ circuit }) => {
  const { chillerLog, setChillerLog, prevChillerLog1, prevChillerLog2 } = useContext(ChillerLogsContext);

  let [expanded, setExpanded] = useState(true);

  let rowData = chillerLog?.circuits?.[0]?.compressors?.[0]?.compressor_details;
  let circuitIndex = circuit?.index;

  const updateCircuitsAndLog = (updatedCompressors: any[], circuitIndex: number) => {
    let updatedCircuits = [...chillerLog?.circuits];
    updatedCircuits[circuitIndex].compressors = updatedCompressors;

    setChillerLog((prevLog) => ({
      ...prevLog,
      circuits: updatedCircuits,
    }));
  };

  const addCompressor = (circuit: ChillerCircuit) => {
    const nextCompressorIndex = circuit?.compressors
      ?.slice()
      .sort((a, b) => a.index - b.index)
      .findIndex((c) => !c.included);

    let compressors = [...circuit?.compressors];
    compressors[nextCompressorIndex].included = true;

    updateCircuitsAndLog(compressors, circuit.index);
  };

  const removeCompressor = (circuit: ChillerCircuit) => {
    const sortedCompressors = circuit?.compressors?.slice().sort((a, b) => b.index - a.index); // sort descending by index

    const lastIncludedCompressorIndex = sortedCompressors?.findIndex((c) => c.included);

    if (lastIncludedCompressorIndex === -1 || lastIncludedCompressorIndex == null) return;

    const compressorToRemove = sortedCompressors[lastIncludedCompressorIndex];

    const updatedCompressors = [...circuit.compressors].map((c) =>
      c.index === compressorToRemove.index ? { ...c, included: false } : c,
    );

    updateCircuitsAndLog(updatedCompressors, circuit.index);
  };

  let includedCompressors = circuit?.compressors?.filter((compressor) => compressor.included);

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary style={{ background: '#A21C37' }}>
        <h3 style={{ color: '#fff', fontWeight: 700, padding: 5 }}>Compressor Details (Circuit {circuitIndex + 1})</h3>
      </AccordionSummary>
      <AccordionDetails style={{ margin: 0, padding: 0 }}>
        <Grid container>
          <Grid
            item
            xs={12}
            style={{ paddingTop: '10px', paddingLeft: '20px', paddingBottom: '10px' }}
            className="no-print"
          >
            <Button
              variant="contained"
              onClick={() => addCompressor(circuit)}
              disabled={includedCompressors?.length > 3}
            >
              + Comp
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => removeCompressor(circuit)}
              style={{ marginLeft: '20px' }}
              disabled={includedCompressors?.length < 2}
            >
              - Comp
            </Button>
          </Grid>
          <Grid item xs={2}>
            <CustomRowLabels data={rowData} />
          </Grid>
          <Grid item xs={10} sx={{ minWidth: 250 }}>
            <Grid container>
              {includedCompressors?.map((compressor, index) => {
                let prevCompressor2 = prevChillerLog2?.circuits?.[circuitIndex]?.compressors[index];
                let prevCompressor1 = prevChillerLog1?.circuits?.[circuitIndex]?.compressors[index];
                let currentCompressor = compressor;

                return (
                  <React.Fragment key={`compressor-${compressor.id}-${index}`}>
                    {prevCompressor2 && (
                      <Grid item xs={1} key={prevCompressor2.id}>
                        <CompressorDetailsSubsection compressor={prevCompressor2} col={1} />
                      </Grid>
                    )}
                    {prevCompressor1 && (
                      <Grid item xs={1} key={prevCompressor1.id}>
                        <CompressorDetailsSubsection compressor={prevCompressor1} col={2} />
                      </Grid>
                    )}
                    <Grid item xs={1} key={compressor.id}>
                      <CompressorDetailsSubsection compressor={currentCompressor} col={3} />
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default CompressorDetailsSection;
